// Chart Browser UI — browse Apple App Store top charts by country without
// entering an app ID. Uses the RSS proxy for live data when enabled, and falls
// back to daily build-time chart-history JSON otherwise.

import { t, getLocale, countryName } from '../i18n/index.js';
import { STOREFRONTS, groupStorefrontsByRegion } from '../data/storefronts.js';
import { genreName } from '../data/genres.js';
import { fetchChartFull } from '../api/rssCharts.js';
import { isChartEnrichmentEnabled } from '../config.js';
import { renderCategoryTrending } from './renderCategoryTrending.js';
import { sortChartEntries, CHART_SORT_KEYS } from '../domain/sortChartEntries.js';
import { ALL_GENRES, computeCategoryBreakdown, filterEntriesByGenre } from '../domain/categoryTrending.js';
import { buildChartReport, buildChartReportFilename } from '../domain/chartReport.js';
import { downloadReport } from '../domain/reportExport.js';
import { chartEntriesToCsvRows, downloadCsv, buildChartCsvFilename } from '../domain/exportCsv.js';
import { chartPenetrationFromSnapshot } from '../domain/marketGap.js';
import { getChartRankDeltas, saveChartRankSnapshot } from '../domain/chartRankSnapshots.js';
import { CHART_SNAPSHOT } from '../data/chartSnapshot.js';
import { loadChartHistorySource } from '../data/chartHistory.js';
import { flagEmoji } from '../domain/flagEmoji.js';
import { CHART_TYPES, parseChartBrowserState, serializeChartBrowserState } from '../domain/chartBrowserUrlState.js';

// Single results tabpanel shared by all three chart-type tabs. Each tab points
// its `aria-controls` here, and the panel's `aria-labelledby` tracks whichever
// tab is active — the APG tabs pattern over one swappable panel.
const RESULTS_PANEL_ID = 'chart-browser-results';
const LIVE_STATUS_ID = 'chart-browser-live-status';

/** @param {string} type @returns {string} stable DOM id for a chart-type tab */
function tabId(type) {
  return `chart-browser-tab-${type}`;
}

/** @param {string} str */
function escHtml(str) {
  return (str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function accentTitleSegment(text, segment) {
  const source = String(text ?? '');
  const needle = String(segment ?? '').trim();
  if (!source || !needle) return escHtml(source);
  const index = source.toLocaleLowerCase().indexOf(needle.toLocaleLowerCase());
  if (index === -1) return escHtml(source);
  const before = source.slice(0, index);
  const match = source.slice(index, index + needle.length);
  const after = source.slice(index + needle.length);
  return `${escHtml(before)}<span class="title-accent">${escHtml(match)}</span>${escHtml(after)}`;
}

/**
 * Render the Chart Browser into `container`.
 *
 * When the RSS proxy is available (isChartEnrichmentEnabled()), shows a live
 * country + chart-type selector with 20 app cards per chart. Each card fires
 * `onSelectApp(trackId)` so the caller can run the full storefront comparison.
 * A secondary card action calls `onCompareApp(trackId)` when provided, or falls
 * back to `onSelectApp(trackId)` when there is no active app to compare against.
 *
 * When the proxy is not available, shows a labelled static chart-history snapshot.
 *
 * @param {HTMLElement} container
 * @param {(trackId: string|number) => void} onSelectApp
 * @param {(trackId: string|number) => void} [onCompareApp]
 */
export function renderChartBrowser(container, onSelectApp, onCompareApp) {
  if (!container) return;
  container.hidden = false;

  _renderBrowserUI(container, onSelectApp, onCompareApp, {
    useSnapshot: !isChartEnrichmentEnabled(),
  });
}

/**
 * Hide and clear the chart browser.
 * @param {HTMLElement|null} container
 */
export function hideChartBrowser(container) {
  if (!container) return;
  container.hidden = true;
  container.innerHTML = '';
}

// ─── browser UI ────────────────────────────────────────────────────────────

function _renderBrowserUI(container, onSelectApp, onCompareApp, { useSnapshot = false } = {}) {
  const initialState = parseChartBrowserState(
    typeof window === 'undefined' ? '' : window.location.search,
  );
  const storefronts = STOREFRONTS;
  const initialCountry = storefronts.some((sf) => sf.code === initialState.country)
    ? initialState.country
    : (storefronts[0]?.code ?? 'us');

  // Build storefront options, grouped into region <optgroup>s so the country
  // list is scannable. Grouping is pure/static (no fetch); the option markup is
  // unchanged, so the first entry (us) is still the default and `change` still
  // triggers a load.
  const locale = getLocale();
  const countryOptions = groupStorefrontsByRegion(storefronts)
    .map(({ region, storefronts }) => {
      const options = storefronts
        .map((sf) => `<option value="${escHtml(sf.code)}">${escHtml(countryName(sf.code, locale, sf.name))} (${sf.code.toUpperCase()})</option>`)
        .join('');
      return `<optgroup label="${escHtml(region)}">${options}</optgroup>`;
    })
    .join('');

  // Build chart type tabs. Roving tabindex (0 for the active tab, -1 otherwise)
  // keeps the whole tablist a single tab stop; aria-controls/aria-selected wire
  // each tab to the shared results panel. Arrow-key navigation is added in JS.
  const tabsHtml = CHART_TYPES.map((type) => {
    const selected = type === initialState.type;
    const cls = selected ? 'chart-browser__tab chart-browser__tab--active' : 'chart-browser__tab';
    return `<button
            role="tab"
            id="${tabId(type)}"
            class="${cls}"
            type="button"
            data-chart-type="${escHtml(type)}"
            aria-controls="${RESULTS_PANEL_ID}"
            aria-selected="${selected ? 'true' : 'false'}"
            tabindex="${selected ? '0' : '-1'}"
          >${escHtml(t(`chartBrowser.type.${type}`))}</button>`;
  }).join('');

  // Build client-side sort-key options. Sorting reorders the already-loaded
  // entries in place — it never refetches, keeping the view local-first.
  const sortOptions = CHART_SORT_KEYS.map(
    (key) => `<option value="${escHtml(key)}">${escHtml(t(`chartBrowser.sort.${key}`))}</option>`,
  ).join('');

  container.innerHTML = `
    <div class="chart-browser" role="region" aria-label="${escHtml(t('chartBrowser.title'))}">
      <header class="chart-browser__header">
        <p class="eyebrow chart-browser__eyebrow">${escHtml(t('nav.radar'))}</p>
        <h2 class="chart-browser__title">${accentTitleSegment(t('chartBrowser.title'), t('nav.radar'))}</h2>
        <p class="chart-browser__subtitle">${escHtml(t('chartBrowser.subtitle'))}</p>
      </header>
      ${useSnapshot ? `<p class="chart-browser__sample-note">${escHtml(t('chartBrowser.sampleSnapshotNote', { date: CHART_SNAPSHOT.capturedAt }))}</p>` : ''}
      <div class="chart-browser__controls">
        <label for="chart-browser-country" class="visually-hidden">${escHtml(t('chartBrowser.countryLabel'))}</label>
        <select id="chart-browser-country" class="chart-browser__select">
          ${countryOptions}
        </select>
        <div class="chart-browser__tabs" role="tablist" aria-label="${escHtml(t('chartBrowser.chartTypeLabel'))}">
          ${tabsHtml}
        </div>
        <div class="chart-browser__genre-filter">
          <label for="chart-browser-genre" class="visually-hidden">${escHtml(t('chartBrowser.genreLabel'))}</label>
          <select id="chart-browser-genre" class="chart-browser__select" aria-label="${escHtml(t('chartBrowser.genreLabel'))}" disabled>
            <option value="${escHtml(ALL_GENRES)}">${escHtml(t('chartBrowser.genreAll'))}</option>
          </select>
        </div>
        <div class="chart-browser__sort">
          <label for="chart-browser-sort" class="visually-hidden">${escHtml(t('chartBrowser.sortLabel'))}</label>
          <select id="chart-browser-sort" class="chart-browser__sort-select" aria-label="${escHtml(t('chartBrowser.sortLabel'))}">
            ${sortOptions}
          </select>
          <button
            type="button"
            id="chart-browser-sort-dir"
            class="chart-browser__sort-dir"
            aria-pressed="false"
          >${escHtml(t('chartBrowser.sortDir.asc'))}</button>
        </div>
        <div class="chart-browser__export">
          <button
            type="button"
            id="chart-browser-export-csv"
            class="chart-browser__export-btn"
            disabled
          >${escHtml(t('chartBrowser.exportCsv'))}</button>
          <button
            type="button"
            id="chart-browser-export"
            class="chart-browser__export-btn"
            disabled
          >${escHtml(t('chartBrowser.exportJson'))}</button>
        </div>
      </div>
      <div id="${RESULTS_PANEL_ID}" class="chart-browser__results" role="tabpanel" aria-labelledby="${tabId(initialState.type)}">
        ${_statePanelHtml('hint', t('chartBrowser.hint'))}
      </div>
      <p id="${LIVE_STATUS_ID}" class="visually-hidden" role="status" aria-live="polite" aria-atomic="true"></p>
      <div class="chart-browser__trending" hidden></div>
    </div>
  `;

  const countrySelect = /** @type {HTMLSelectElement} */ (container.querySelector('#chart-browser-country'));
  const resultsEl = /** @type {HTMLElement} */ (container.querySelector('.chart-browser__results'));
  const trendingEl = /** @type {HTMLElement} */ (container.querySelector('.chart-browser__trending'));
  const genreSelect = /** @type {HTMLSelectElement} */ (container.querySelector('#chart-browser-genre'));
  const sortSelect = /** @type {HTMLSelectElement} */ (container.querySelector('#chart-browser-sort'));
  const sortDirBtn = /** @type {HTMLButtonElement} */ (container.querySelector('#chart-browser-sort-dir'));
  const exportBtn = /** @type {HTMLButtonElement} */ (container.querySelector('#chart-browser-export'));
  const exportCsvBtn = /** @type {HTMLButtonElement} */ (container.querySelector('#chart-browser-export-csv'));
  const liveStatusEl = /** @type {HTMLElement} */ (container.querySelector(`#${LIVE_STATUS_ID}`));
  const snapshotNoteEl = /** @type {HTMLElement|null} */ (container.querySelector('.chart-browser__sample-note'));

  countrySelect.value = initialCountry;
  sortSelect.value = initialState.sortKey;

  let currentType = initialState.type;
  // Sort state lives here so reordering can happen over the cached entries
  // without a network round-trip. Default mirrors Apple's published chart order.
  let sortKey = initialState.sortKey;
  let sortDir = initialState.sortDir;
  let selectedGenre = ALL_GENRES;
  /** @type {import('../domain/chartRank.js').ChartBrowserEntry[]} */
  let loadedEntries = [];
  let liveStatus = '';

  function setLiveStatus(message) {
    const next = message ?? '';
    if (next === liveStatus) return;
    liveStatus = next;
    liveStatusEl.textContent = next;
  }

  function syncSortDirButton() {
    const ascending = sortDir === 'asc';
    sortDirBtn.textContent = t(ascending ? 'chartBrowser.sortDir.asc' : 'chartBrowser.sortDir.desc');
    sortDirBtn.setAttribute('aria-pressed', ascending ? 'false' : 'true');
  }

  function syncUrlState() {
    if (typeof window === 'undefined' || !window.history?.replaceState) return;
    const url = new URL(window.location.href);
    url.search = serializeChartBrowserState({
      country: countrySelect.value,
      type: currentType,
      sortKey,
      sortDir,
    }, url.searchParams).toString();
    window.history.replaceState({}, '', url);
  }

  function syncGenreOptions() {
    const breakdown = computeCategoryBreakdown(loadedEntries);
    const availableGenres = new Set(breakdown.map((item) => item.genre));
    if (selectedGenre !== ALL_GENRES && !availableGenres.has(selectedGenre)) {
      selectedGenre = ALL_GENRES;
    }

    const locale = getLocale();
    const genreOptions = breakdown
      .map(({ genre, genreId }) => {
        const label = genreName(genreId, locale, genre);
        return `<option value="${escHtml(genre)}">${escHtml(label)}</option>`;
      })
      .join('');

    genreSelect.innerHTML = `
      <option value="${escHtml(ALL_GENRES)}">${escHtml(t('chartBrowser.genreAll'))}</option>
      ${genreOptions}
    `;
    genreSelect.value = selectedGenre;
    genreSelect.disabled = breakdown.length === 0;
  }

  // Re-render the loaded entries in their current sort order. Pure client-side:
  // no fetch, no proxy call — just filter/sort data already in memory.
  function renderDisplayed() {
    if (loadedEntries.length === 0) return;
    _renderEntries(resultsEl, displayedEntries(), onSelectApp, onCompareApp);
    syncExportState();
  }

  // The export action is only meaningful once a chart is loaded, so it tracks
  // whether there are entries in memory. Sorting never refetches, so the export
  // simply re-derives the current on-screen order at click time.
  function syncExportState() {
    const disabled = displayedEntries().length === 0;
    exportBtn.disabled = disabled;
    exportCsvBtn.disabled = disabled;
  }

  function displayedEntries() {
    return sortChartEntries(filterEntriesByGenre(loadedEntries, selectedGenre), sortKey, sortDir);
  }

  async function loadChart() {
    const country = countrySelect.value;
    resultsEl.innerHTML = _loadingSkeletonHtml(t('chartBrowser.loading'));
    setLiveStatus(t('chartBrowser.loading'));
    trendingEl.hidden = true;
    trendingEl.innerHTML = '';
    loadedEntries = [];
    selectedGenre = ALL_GENRES;
    syncGenreOptions();
    syncExportState();
    try {
      const entries = useSnapshot
        ? await loadSnapshotChart(country, currentType, snapshotNoteEl)
        : await fetchChartFull(country, currentType, 20);
      const currentEntries = Array.isArray(entries) ? entries : [];
      if (useSnapshot) {
        loadedEntries = currentEntries;
      } else {
        const { deltas } = getChartRankDeltas(country, currentType, currentEntries);
        loadedEntries = _annotateChartRankDeltas(currentEntries, deltas);
      }
      syncGenreOptions();
      if (loadedEntries.length === 0) {
        _renderEntries(resultsEl, loadedEntries, onSelectApp, onCompareApp);
        setLiveStatus(t('chartBrowser.empty'));
      } else {
        renderDisplayed();
        setLiveStatus('');
      }
      renderCategoryTrending(trendingEl, loadedEntries);
      if (!useSnapshot) {
        saveChartRankSnapshot(country, currentType, currentEntries, new Date().toISOString());
      }
    } catch (err) {
      loadedEntries = [];
      selectedGenre = ALL_GENRES;
      syncGenreOptions();
      const message = err instanceof Error ? err.message : String(err);
      const errorText = t('chartBrowser.error', { message });
      resultsEl.innerHTML = _errorPanelHtml(errorText);
      const retryBtn = resultsEl.querySelector('.chart-browser__retry');
      retryBtn?.addEventListener('click', loadChart);
      setLiveStatus(errorText);
    } finally {
      syncExportState();
    }
  }

  sortSelect.addEventListener('change', () => {
    sortKey = sortSelect.value;
    renderDisplayed();
    syncUrlState();
  });

  genreSelect.addEventListener('change', () => {
    selectedGenre = genreSelect.value;
    renderDisplayed();
  });

  sortDirBtn.addEventListener('click', () => {
    sortDir = sortDir === 'asc' ? 'desc' : 'asc';
    syncSortDirButton();
    renderDisplayed();
    syncUrlState();
  });

  // Export the loaded chart as JSON, in the exact order currently shown. Built
  // entirely from the in-memory entries — no fetch, no backend.
  exportBtn.addEventListener('click', () => {
    if (loadedEntries.length === 0) return;
    const country = countrySelect.value;
    const ordered = displayedEntries();
    const report = buildChartReport(country, currentType, ordered, new Date().toISOString());
    const today = new Date().toISOString().slice(0, 10);
    downloadReport(report, buildChartReportFilename(country, currentType, today));
  });

  // CSV export follows the same displayed order as JSON, but emits only the
  // spreadsheet-friendly columns requested for chart browsing.
  exportCsvBtn.addEventListener('click', () => {
    if (loadedEntries.length === 0) return;
    const country = countrySelect.value;
    const csv = chartEntriesToCsvRows(displayedEntries());
    if (!csv) return;
    const today = new Date().toISOString().slice(0, 10);
    downloadCsv(csv, buildChartCsvFilename(country, currentType, today));
  });

  countrySelect.addEventListener('change', () => {
    syncUrlState();
    loadChart();
  });

  // ── Chart-type tablist (APG tabs pattern) ────────────────────────────────
  // Activating a tab — by click, Enter/Space (native button), or arrow-key
  // focus — makes it the sole tab stop, repoints the results panel's label, and
  // reloads. Activation follows focus (automatic activation): the same load a
  // click already triggered, so arrowing through tabs swaps the chart live.
  const tabEls = /** @type {HTMLButtonElement[]} */ (
    Array.from(container.querySelectorAll('.chart-browser__tab'))
  );

  function activateTab(tab, { focus = false } = {}) {
    tabEls.forEach((other) => {
      const isActive = other === tab;
      other.setAttribute('aria-selected', isActive ? 'true' : 'false');
      other.setAttribute('tabindex', isActive ? '0' : '-1');
      other.classList.toggle('chart-browser__tab--active', isActive);
    });
    resultsEl.setAttribute('aria-labelledby', tab.id);
    currentType = tab.dataset.chartType ?? 'top-free';
    if (focus) tab.focus();
    syncUrlState();
    loadChart();
  }

  tabEls.forEach((tab, index) => {
    tab.addEventListener('click', () => activateTab(tab));
    tab.addEventListener('keydown', (event) => {
      const lastIndex = tabEls.length - 1;
      let nextIndex;
      switch (event.key) {
        case 'ArrowRight':
          nextIndex = index === lastIndex ? 0 : index + 1;
          break;
        case 'ArrowLeft':
          nextIndex = index === 0 ? lastIndex : index - 1;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = lastIndex;
          break;
        default:
          return;
      }
      event.preventDefault();
      activateTab(tabEls[nextIndex], { focus: true });
    });
  });

  syncSortDirButton();
  // Load immediately with defaults, or with valid chart params from the URL.
  loadChart();
}

async function loadSnapshotChart(country, chartType, snapshotNoteEl = null) {
  const source = await loadChartHistorySource();
  if (snapshotNoteEl) {
    snapshotNoteEl.textContent = t('chartBrowser.sampleSnapshotNote', { date: source.capturedAt });
  }
  return source.getChart(country, chartType);
}

function _statePanelHtml(kind, message) {
  return `
    <div class="chart-browser__state chart-browser__state--${escHtml(kind)}">
      <p class="chart-browser__state-copy">${escHtml(message)}</p>
    </div>
  `;
}

function _loadingSkeletonHtml(message) {
  const rows = Array.from({ length: 6 }, (_, index) => `
    <li class="chart-browser__skeleton-row">
      <span class="chart-browser__skeleton-rank">${index + 1}</span>
      <span class="chart-browser__skeleton-icon"></span>
      <span class="chart-browser__skeleton-body">
        <span class="chart-browser__skeleton-line chart-browser__skeleton-line--name"></span>
        <span class="chart-browser__skeleton-line chart-browser__skeleton-line--meta"></span>
      </span>
      <span class="chart-browser__skeleton-action"></span>
    </li>
  `).join('');

  return `
    <div class="chart-browser__loading-state" aria-label="${escHtml(message)}">
      <ul class="chart-browser__skeleton" aria-hidden="true">
        ${rows}
      </ul>
    </div>
  `;
}

function _errorPanelHtml(message) {
  return `
    <div class="chart-browser__state chart-browser__state--error">
      <p class="chart-browser__state-copy">${escHtml(message)}</p>
      <button type="button" class="chart-browser__retry">${escHtml(t('chartBrowser.retry'))}</button>
    </div>
  `;
}

/**
 * Build the subtle "also charts in N of your sample countries" line for a card.
 *
 * Computed entirely from the committed CHART_SNAPSHOT — no proxy, no network. We
 * only surface it when the snapshot covers more than one country (otherwise there
 * is no cross-country breadth to report) and the app appears in at least two of
 * them (a single appearance is not a notably wide footprint). Otherwise it
 * degrades silently to an empty string.
 *
 * @param {string|number|null|undefined} trackId
 * @returns {string} HTML for the line, or '' when nothing to show.
 */
function _penetrationLine(trackId) {
  const pen = chartPenetrationFromSnapshot(trackId, CHART_SNAPSHOT);
  if (pen.totalCountries < 2 || pen.count < 2) return '';

  const names = pen.countries
    .map((cc) => `${flagEmoji(cc)} ${countryName(cc, getLocale(), cc.toUpperCase())}`)
    .join(', ');
  const label = t('chartBrowser.penetration', { n: String(pen.count), total: String(pen.totalCountries) });
  const title = t('chartBrowser.penetrationTitle', { countries: names });

  return `<span class="chart-browser__card-penetration" title="${escHtml(title)}">${escHtml(label)}</span>`;
}

/**
 * @param {import('../domain/chartRank.js').ChartBrowserEntry[]} entries
 * @param {Map<string, import('../domain/chartRankSnapshots.js').ChartRankDelta & { snappedAt: string }>} deltas
 * @returns {Array<import('../domain/chartRank.js').ChartBrowserEntry & { chartRankDelta?: import('../domain/chartRankSnapshots.js').ChartRankDelta & { snappedAt: string } }>}
 */
function _annotateChartRankDeltas(entries, deltas) {
  if (!Array.isArray(entries) || !(deltas instanceof Map) || deltas.size === 0) return entries;
  return entries.map((entry) => {
    const delta = deltas.get(String(entry.trackId));
    return delta ? { ...entry, chartRankDelta: delta } : entry;
  });
}

/**
 * @param {import('../domain/chartRankSnapshots.js').ChartRankDelta & { snappedAt?: string } | null | undefined} delta
 * @returns {string}
 */
function _chartRankDeltaText(delta) {
  if (!delta || delta.status === 'same') return '';
  const date = (delta.snappedAt ?? '').slice(0, 10);
  if (delta.status === 'new') {
    return t('chartBrowser.rankDelta.ariaNew', { date });
  }
  const key = delta.status === 'up'
    ? 'chartBrowser.rankDelta.ariaUp'
    : 'chartBrowser.rankDelta.ariaDown';
  return t(key, { n: String(delta.magnitude), date });
}

/**
 * @param {import('../domain/chartRankSnapshots.js').ChartRankDelta & { snappedAt?: string } | null | undefined} delta
 * @returns {string}
 */
function _chartRankDeltaBadge(delta) {
  if (!delta || delta.status === 'same') return '';

  const title = escHtml(_chartRankDeltaText(delta));
  if (delta.status === 'new') {
    return `<span class="chart-browser__rank-delta chart-browser__rank-delta--new" aria-label="${title}" title="${title}">${escHtml(t('chartBrowser.rankDelta.new'))}</span>`;
  }

  const key = delta.status === 'up' ? 'delta.up' : 'delta.down';
  return `<span class="chart-browser__rank-delta chart-browser__rank-delta--${escHtml(delta.status)}" aria-label="${title}" title="${title}">${escHtml(t(key, { n: String(delta.magnitude) }))}</span>`;
}

/**
 * Render chart entries as cards.
 * @param {HTMLElement} container
 * @param {import('../domain/chartRank.js').ChartBrowserEntry[]} entries
 * @param {(trackId: string|number) => void} onSelectApp
 * @param {(trackId: string|number) => void} [onCompareApp]
 */
function _renderEntries(container, entries, onSelectApp, onCompareApp) {
  if (!entries || entries.length === 0) {
    container.innerHTML = _statePanelHtml('empty', t('chartBrowser.empty'));
    return;
  }

  const locale = getLocale();
  const items = entries
    .map((entry) => {
      const artwork = entry.artworkUrl
        ? `<img class="chart-browser__card-icon" src="${escHtml(entry.artworkUrl)}" alt="" width="60" height="60" loading="lazy">`
        : `<span class="chart-browser__card-icon chart-browser__card-icon--placeholder" aria-hidden="true"></span>`;

      // Localize the genre chip by stable id; fall back to the feed's English name.
      const genreLabel = entry.primaryGenre
        ? genreName(entry.genreId, locale, entry.primaryGenre)
        : '';
      const genre = genreLabel
        ? `<span class="chart-browser__card-genre">${escHtml(genreLabel)}</span>`
        : '';

      const penetration = _penetrationLine(entry.trackId);
      const trackId = escHtml(String(entry.trackId ?? ''));
      const deltaBadge = _chartRankDeltaBadge(entry.chartRankDelta);
      const deltaText = _chartRankDeltaText(entry.chartRankDelta);

      // Label reads rank + name + genre so screen-reader users hear the same
      // at-a-glance context the card shows visually. Genre is optional in the
      // feed, so fall back to the genre-less label when it is missing.
      const baseAriaLabel = genreLabel
        ? t('chartBrowser.cardAriaLabelGenre', {
            rank: String(entry.rank),
            name: entry.name ?? '',
            genre: genreLabel,
          })
        : t('chartBrowser.cardAriaLabel', {
            rank: String(entry.rank),
            name: entry.name ?? '',
          });
      const ariaLabel = deltaText ? `${baseAriaLabel}, ${deltaText}` : baseAriaLabel;
      const compareLabel = t('chartBrowser.compareAgainstCurrent');
      const compareAriaLabel = t('chartBrowser.compareAgainstCurrentAriaLabel', {
        name: entry.name ?? '',
      });

      return `
        <li class="chart-browser__card-wrap">
          <button
            class="chart-browser__card"
            type="button"
            data-track-id="${trackId}"
            aria-label="${escHtml(ariaLabel)}"
          >
            <span class="chart-browser__card-rank" aria-hidden="true">#${entry.rank}</span>
            ${artwork}
            <span class="chart-browser__card-info">
              <span class="chart-browser__card-name-row">
                <span class="chart-browser__card-name">${escHtml(entry.name ?? '')}</span>
                ${deltaBadge}
              </span>
              <span class="chart-browser__card-artist">${escHtml(entry.artistName ?? '')}</span>
              ${genre}
              ${penetration}
            </span>
          </button>
          <button
            class="chart-browser__compare-btn"
            type="button"
            data-track-id="${trackId}"
            aria-label="${escHtml(compareAriaLabel)}"
          >${escHtml(compareLabel)}</button>
        </li>
      `;
    })
    .join('');

  container.innerHTML = `<ul class="chart-browser__cards" role="list">${items}</ul>`;

  container.querySelectorAll('.chart-browser__card').forEach((card) => {
    card.addEventListener('click', () => {
      const id = /** @type {HTMLElement} */ (card).dataset.trackId;
      if (id) onSelectApp(id);
    });
  });

  container.querySelectorAll('.chart-browser__compare-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = /** @type {HTMLElement} */ (btn).dataset.trackId;
      if (!id) return;
      if (onCompareApp) {
        onCompareApp(id);
      } else {
        onSelectApp(id);
      }
    });
  });
}
